/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { forwardRef, Fragment, type JSX } from "react";

import { spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { IconElement } from "../../icons/Icon/Icon";
import { ChevronRightIcon } from "../../icons/Icon/NavigationIcons";
import { baseStyle, disabledStyle } from "../listStyles.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const textStyle: Interpolation = { flexGrow: 1, width: spacing.t };

export type ListRecentSearchProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  disabled?: boolean;
  icon?: IconElement;
  title: string;
};

export const ListRecentSearch = forwardRef<any, ListRecentSearchProps>(
  (
    {
      asChild = false,
      children,
      disabled,
      icon = <ChevronRightIcon />,
      title,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <Text
          overflow="ellipsis"
          key="text"
          size="m"
          type="body"
          css={textStyle}
        >
          {title}
        </Text>,
        <Fragment key="icon">{icon}</Fragment>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[baseStyle, { textAlign: "left" }, disabled && disabledStyle]}
        {...{ disabled, ...props }}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ListRecentSearch.displayName = "ListRecentSearch";
